/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from 'react'

import Grid from '@mui/material/Grid'
import { useQuery } from '@tanstack/react-query'

import MDBox from 'components/MDBox'
import { useAuth } from 'context/auth'
import { setConfigurationOrganisation, useMaterialUIController } from 'context/materialUI'
import { OrganisationConfiguration } from 'context/materialUI/types'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { fetchOrganisationConfiguration, updateOrgConfiguration } from 'utils/config'

import MainContent from './components/MainContent'
import { FormContext } from './constants'
import ManageCard from '../../../modules/ManageCard'

function ConfigOrganisation() {
	const [controller, dispatch] = useMaterialUIController()
	const { user } = useAuth()
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const { configuration } = controller

	const [localForm, setLocalForm] = useState<OrganisationConfiguration>(configuration?.organisation)

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['configuration-organisation'],
		queryFn: () => fetchOrganisationConfiguration(user.auth),
		enabled: !!user, // don't run until user is available
	})

	useEffect(() => {
		if (data) {
			setConfigurationOrganisation(dispatch, data)
			setLocalForm(data)
		} else {
			setLocalForm(configuration?.organisation)
		}
	}, [data, configuration?.organisation, dispatch])

	if (!isLoading || !error) {
		return (
			<FormContext.Provider value={{ localForm, setLocalForm, formEdited, setFormEdited }}>
				<DashboardLayout>
					<DashboardNavbar isMini />
					<MDBox mt={4}>
						<Grid container spacing={3}>
							<Grid item xs={12} lg={9}>
								<MDBox mb={3}>
									<MainContent />
								</MDBox>
							</Grid>
							<Grid item xs={12} lg={3}>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<ManageCard
											formEdited={formEdited}
											setFormEdited={setFormEdited}
											save={{
												onClicked: async () => {
													await updateOrgConfiguration(user.auth, localForm)
													refetch()
												},
											}}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</MDBox>
				</DashboardLayout>
			</FormContext.Provider>
		)
	} else {
		return <LoadingComponent type="box" height={600} />
	}
}

export default ConfigOrganisation
